import { LoaderPage } from '@axa-fr-axion/loader';
import { FunctionalErrorResponse, RestError } from '@axa-fr-axion/restful';
import { useNavigation } from '@axa-fr-axion/router';
import { Dossier } from '@axa-fr-axion/user-journey-services';
import { AppOptions, AppProps, JourneyAction, JourneyData, JourneyDataValidityKeys, JourneyDataValue, JourneyInitialData, JourneyReducer } from 'App.interface';
import { journeyReducer } from 'App.reducer';
import { DEFAULT_OPTIONS, chooseEndStep, getInitialStep, initJourneyDataWithOrigin } from 'App.utils';
import { NavigationPath, StepNavigationPath, TechNavigationPath, ValidityStatus } from 'interfaces';
import upperFirst from 'lodash.upperfirst';
import { PresetDefinition, selectPreset } from 'presets';
import { PropsWithChildren, createContext, useCallback, useContext, useEffect, useReducer, useState } from 'react';
import { sendTagEventNavigation, shouldSendTagEventNavigation } from 'services/tag';
import { useMediaQuery } from 'usehooks-ts';
import { isAcceptedValidityStatus } from 'utils';

export const LoaderComponent = <LoaderPage title="Veuillez patienter, nous chargeons la prochaine étape..." />;

const ENABLE_ANALYTICS: Partial<Record<NavigationPath, boolean>> = {
    ChooseAdvisor: false,
};
export interface IApplicationContext {
    _options: AppOptions;
    preset: PresetDefinition;
    isMobile: boolean;

    // Navigation
    initialStep: StepNavigationPath;
    step: NavigationPath | TechNavigationPath;
    prevStep?: NavigationPath | TechNavigationPath;
    goToStep: (newStep: NavigationPath) => void;
    replace: (newStep: NavigationPath) => void
    goBack: (step?: number | NavigationPath) => void;
    handleTechnicalError: () => void;
    onScenarioChange?: AppProps['onScenarioChange'];

    // Reducer
    journeyData: JourneyData;
    dispatchJourneyData: React.Dispatch<JourneyAction>;
    handleChange: (type: keyof Omit<JourneyData, 'folder'>) => (newData: Omit<JourneyDataValue, 'folder'>, validityStatus: ValidityStatus) => void;

    handleInitializationSuccess: (folder: Dossier) => void;
    handleFunctionalError: (error?: FunctionalErrorResponse) => void;
    LoaderComponent: JSX.Element;

    //Analytics
    setAnalytics: (value: Partial<Record<NavigationPath, boolean>>) => void

}

const ApplicationContext = createContext<IApplicationContext | undefined>(undefined);

const ApplicationProvider: React.FC<PropsWithChildren & AppProps> = ({
    onScenarioChange,
    options,
    data,
    children,
}) => {
    const _options: AppOptions = {
        ...DEFAULT_OPTIONS,
        ...options,
    };

    const preset = selectPreset(_options.config?.preset);
    const initialStep = getInitialStep(_options, preset);

    const { currentStep: step, history, push: goToStep, replace, goBack } = useNavigation<NavigationPath>();
    const prevStep = history.at(1);

    const [ analytics, setAnalytics ] = useState(ENABLE_ANALYTICS);

    const [ journeyData, dispatchJourneyData ] =
        useReducer<JourneyReducer, JourneyInitialData>(
            journeyReducer,
            data,
            // NOTE: Tant que GLM n'est pas en mesure d'ingérer le code MPE, nous somme contraint
            // de forcer la sélection du produit MRP pour le preset MicroEnt.
            initJourneyDataWithOrigin(options?.origine),
        );

    const handleTechnicalError = (err?: Error): void => {
        const error = err as RestError;
        if (!error?.isDatadomeBlocked) {
            goToStep('Error');
        }
    };

    const handleChange = useCallback((type: keyof Omit<JourneyData, 'folder'>) => {
        const validityKey = `is${ upperFirst(type) }Valid` as JourneyDataValidityKeys;
        return (newData: Omit<JourneyDataValue, 'folder'>, validityStatus: ValidityStatus): void => {
            dispatchJourneyData({
                type,
                payload: {
                    [ type ]: newData,
                    [ validityKey ]: isAcceptedValidityStatus(validityStatus),
                },
            });
        };
    }, [ dispatchJourneyData ]);

    const handleInitializationSuccess = (folder: Dossier): void => {
        dispatchJourneyData({ type: 'INITIALIZE', folder });
    };

    const handleFunctionalError = (error?: FunctionalErrorResponse): void => {
        goToStep(chooseEndStep(error?.status?.funnel));
    };

    const isMobile = !useMediaQuery('(min-width: 1024px)');

    const enableAnalytics = analytics[ step ] === undefined || analytics[ step ];

    useEffect(() => {
        if (enableAnalytics && shouldSendTagEventNavigation(step)) {
            setAnalytics({ [ step ]: false });
            sendTagEventNavigation(step, journeyData, options);
        }
    }, [ step, journeyData, options, enableAnalytics ]);

    return (
        <ApplicationContext.Provider value={
            {
                _options,
                preset,
                isMobile,

                // Navigation
                initialStep,
                step,
                prevStep,
                goToStep,
                replace,
                goBack,
                handleTechnicalError,
                onScenarioChange,

                // Reducer
                journeyData,
                dispatchJourneyData,
                handleChange,

                handleInitializationSuccess,
                handleFunctionalError,

                LoaderComponent,
                setAnalytics,
            }
        }
        >
            { children }
        </ApplicationContext.Provider>
    );
};

const useApplication = (): IApplicationContext => {
    const context = useContext(ApplicationContext);

    if (!context) {
        throw new Error('useApplication must be used within a ApplicationProvider');
    }

    return context;
};

export { useApplication };
export default ApplicationProvider;
